import React from "react";
import {
  AppShell,
  Burger,
  Group,
  Text,
  NavLink,
  ScrollArea,
  Avatar,
  Menu,
  Alert,
  Box,
  Stack,
  Divider,
  Button,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useNavigate, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import { Logo } from "../../generic/Logo";
import { AppIcon } from "../../generic/AppIcon";
import { useApp } from "../../../contexts/AppContext";
import { SiteMapItem } from "../../../config/siteMap";
import { canUserAccess } from "@/utils";
import { NoAccessMessage } from "@/components/generic";
import {
  useCategoriesContext,
  useDisplayLayoutContext,
  useVenuesContext,
} from "@/contexts";

export const DashboardLayout: React.FC<any> = (props: any) => {
  const {
    children,
    page,
    footerMessage,
    footerMessageType = "info",
    showFooterMessage = false,
  } = props;

  const [opened, { toggle, close }] = useDisclosure();
  const [userMenuOpened, { toggle: toggleUserMenu, close: closeUserMenu }] =
    useDisclosure();
  const navigate = useNavigate();
  const location = useLocation();

  // Get data from AppContext
  const {
    user,
    userRoles,
    getVisibleRoutes,
    signOut,
    subscriptionStatus,
    trialEndsAt,
  } = useApp();
  const { reset: resetVenues } = useVenuesContext();
  const { reset: resetCategories } = useCategoriesContext();
  const { reset: resetDisplayLayouts } = useDisplayLayoutContext();

  // Get filtered navigation items from context
  const navigationItems = getVisibleRoutes();

  const { name, subtitle } = page || {};

  const handleNavClick = (path: string) => {
    navigate(path);
    close(); // Close mobile sidebar when navigating
  };

  const handleSignOut = () => {
    resetVenues();
    resetCategories();
    resetDisplayLayouts();
    signOut();
    navigate("/account/signin");
    closeUserMenu();
  };

  const handleProfileClick = () => {
    navigate("/account/profile");
    closeUserMenu();
  };

  const handleAccountSettingsClick = () => {
    navigate("/account/settings");
    closeUserMenu();
  };

  const handleAccountSubscriptionClick = () => {
    navigate("/account/subscription");
    closeUserMenu();
  };

  const getFooterIcon = () => {
    return (
      <AppIcon
        iconName={
          footerMessageType === "success"
            ? "Check"
            : footerMessageType === "warning"
            ? "AlertTriangle"
            : footerMessageType === "error"
            ? "X"
            : "InfoCircle"
        }
        size={16}
      />
    );
  };

  const getFooterColor = () => {
    switch (footerMessageType) {
      case "success":
        return "green";
      case "warning":
        return "yellow";
      case "error":
        return "red";
      default:
        return "blue";
    }
  };

  // Subscription footer logic
  const getSubscriptionFooterConfig = () => {
    // Don't show subscription footer on subscription page
    if (location.pathname === "/account/subscription") {
      return null;
    }

    if (subscriptionStatus === "trial" && trialEndsAt) {
      const daysRemaining = dayjs(trialEndsAt).diff(dayjs(), "day");

      if (daysRemaining > 3) {
        // if user has more than 3 days of trial then show nothing,
        // let them enjoy the app
        return null;
      }

      return {
        message: `Your trial ends in ${daysRemaining} ${
          daysRemaining === 1 ? "day" : "days"
        }. Upgrade now to continue using VenueBoards.`,
        color: "yellow",
        icon: "AlertTriangle",
        buttonText: "Upgrade Now",
      };
    }

    if (subscriptionStatus === "expired") {
      return {
        message:
          "Your trial has ended. Subscribe now to continue using VenueBoards.",
        color: "red",
        icon: "AlertCircle",
        buttonText: "Subscribe Now",
      };
    }

    if (subscriptionStatus === "cancelled") {
      return {
        message:
          "Your subscription has been cancelled. Reactivate to continue using VenueBoards.",
        color: "orange",
        icon: "AlertTriangle",
        buttonText: "Reactivate",
      };
    }

    return null;
  };

  const subscriptionFooter = getSubscriptionFooterConfig();
  const showSubscriptionFooter = Boolean(subscriptionFooter);

  // Determine footer height and content
  const hasFooter = showFooterMessage || showSubscriptionFooter;
  const footerHeight = hasFooter ? { base: 90, sm: 60 } : 0;

  // Check if current path matches the item or any of its children
  const isItemActive = (item: SiteMapItem): boolean => {
    // Check if current item matches (only if it has a URL)
    if (item.url && location.pathname === item.url) {
      return true;
    }

    // Check if any child item matches
    if (item.items && item.items.length > 0) {
      return item.items.some((child) => isItemActive(child));
    }

    return false;
  };

  // Render navigation items recursively
  const renderNavItem = (item: SiteMapItem, index: number) => {
    // Check if item has children (must be array with length > 0)
    const hasChildren = Array.isArray(item.items) && item.items.length > 0;

    // Parent item with children (like "Events")
    if (hasChildren) {
      const isActive = isItemActive(item);

      return (
        <NavLink
          key={`parent-${item.name}-${index}`}
          active={isActive}
          label={item.name}
          leftSection={
            <AppIcon iconName={item.icon || "Dashboard"} size={18} />
          }
          childrenOffset={28}
          defaultOpened={isActive}
          style={{
            borderRadius: "8px",
          }}
        >
          {item.items!.map((child, childIndex) =>
            renderNavItem(child, childIndex)
          )}
        </NavLink>
      );
    }

    // Leaf item with URL (clickable navigation item)
    if (item.url) {
      return (
        <NavLink
          key={`leaf-${item.url}-${index}`}
          active={location.pathname === item.url}
          label={item.name}
          leftSection={
            <AppIcon iconName={item.icon || "Dashboard"} size={18} />
          }
          onClick={() => handleNavClick(item.url!)}
          style={{
            borderRadius: "8px",
          }}
        />
      );
    }

    // Item without children and without URL - don't render
    return null;
  };

  const sidebar = (
    <Stack h="100%" w="100%" gap={0} bg={"var(--mantine-color-gray-0)"}>
      {/* Navigation Items */}
      <ScrollArea flex={1} offsetScrollbars>
        <Stack gap="xs" p={"xs"} pr={0}>
          {navigationItems.map((item, index) => renderNavItem(item, index))}
        </Stack>
      </ScrollArea>

      {/* Bottom Section */}
      <Box p="md">
        <Divider mb="md" />

        {/* User Menu */}
        {user && (
          <Menu
            shadow="md"
            width={250}
            position="top-end"
            opened={userMenuOpened}
            onChange={toggleUserMenu}
            withinPortal={true}
            trapFocus={false}
            closeOnItemClick={false}
          >
            <Menu.Target>
              <Group
                gap="sm"
                onClick={toggleUserMenu}
                style={{
                  padding: "8px 12px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  backgroundColor: userMenuOpened
                    ? "var(--mantine-color-gray-1)"
                    : "transparent",
                }}
                __vars={{
                  "--group-hover-bg": "var(--mantine-color-gray-0)",
                }}
                mod={{
                  hover: !userMenuOpened,
                }}
              >
                <Avatar size="sm" color="blue">
                  {user.username
                    ?.split(" ")
                    .map((n) => n[0])
                    .join("")}
                </Avatar>
                <Box style={{ flex: 1, minWidth: 0 }}>
                  <Text size="sm" fw={500} truncate>
                    {user.username}
                  </Text>
                  <Text size="xs" c="dimmed" truncate>
                    {user.email}
                  </Text>
                </Box>
                <AppIcon
                  iconName="ChevronUp"
                  size={14}
                  style={{
                    transform: userMenuOpened
                      ? "rotate(0deg)"
                      : "rotate(180deg)",
                    transition: "transform 150ms ease",
                  }}
                />
              </Group>
            </Menu.Target>

            <Menu.Dropdown>
              <Menu.Item
                leftSection={<AppIcon iconName="User" size={16} />}
                onClick={handleProfileClick}
              >
                Profile
              </Menu.Item>
              <Menu.Item
                leftSection={<AppIcon iconName="Settings" size={16} />}
                onClick={handleAccountSettingsClick}
              >
                Account Settings
              </Menu.Item>
              <Menu.Item
                leftSection={<AppIcon iconName="CreditCard" size={16} />}
                onClick={handleAccountSubscriptionClick}
              >
                Subscription
              </Menu.Item>
              <Menu.Divider />
              <Menu.Item
                leftSection={<AppIcon iconName="Logout" size={16} />}
                color="red"
                onClick={handleSignOut}
              >
                Sign Out
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        )}

        {/* App Version - at the very bottom */}
        <Text size="xs" c="dimmed" ta="center" mt="xs">
          version {import.meta.env.VITE_APP_VERSION}
        </Text>
      </Box>
    </Stack>
  );

  const hasAccess = canUserAccess(props.page.roles, userRoles);

  return (
    <AppShell
      header={{ height: 70 }}
      navbar={{
        width: 250,
        breakpoint: "md",
        collapsed: { mobile: !opened },
      }}
      footer={{ height: footerHeight }}
      padding="sm"
    >
      {/* Header */}
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between">
          <Group>
            <Burger
              opened={opened}
              onClick={toggle}
              hiddenFrom="md"
              size="sm"
            />
            <Box visibleFrom="md">
              <Logo size="small" />
            </Box>
            <Box>
              <Text size="lg" fw={600} c="dark">
                {name}
              </Text>
              {subtitle && (
                <Text visibleFrom="md" size="sm" c="dimmed">
                  {subtitle}
                </Text>
              )}
            </Box>
          </Group>
        </Group>
      </AppShell.Header>

      {/* Sidebar */}
      <AppShell.Navbar p={0}>{sidebar}</AppShell.Navbar>

      {/* Footer */}
      {hasFooter && (
        <AppShell.Footer>
          {/* Subscription Footer */}
          {showSubscriptionFooter && subscriptionFooter && (
            <Alert
              icon={<AppIcon iconName={subscriptionFooter.icon} size={16} />}
              color={subscriptionFooter.color}
              variant="light"
              style={{
                border: "none",
                borderRadius: 0,
                margin: 0,
                display: "flex",
                alignItems: "center",
              }}
            >
              <Group justify="space-between" w="100%" gap="sm" wrap="nowrap">
                <Text size="sm" style={{ flex: 1 }}>
                  {subscriptionFooter.message}
                </Text>
                <Button
                  size="xs"
                  color={subscriptionFooter.color}
                  onClick={() => navigate("/account/subscription")}
                >
                  {subscriptionFooter.buttonText}
                </Button>
              </Group>
            </Alert>
          )}

          {/* Custom Footer Message */}
          {showFooterMessage && footerMessage && !showSubscriptionFooter && (
            <Alert
              icon={getFooterIcon()}
              color={getFooterColor()}
              variant="light"
              style={{
                border: "none",
                borderRadius: 0,
                margin: 0,
              }}
            >
              {footerMessage}
            </Alert>
          )}
        </AppShell.Footer>
      )}

      {/* Main Content */}
      <AppShell.Main>
        {!hasAccess && <NoAccessMessage />}
        {hasAccess && children}
      </AppShell.Main>
    </AppShell>
  );
};
